<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useMessageContext } from '../provider.js';
import { MESSAGE_TYPES } from '../constants';
import BaseAttachmentBubble from './BaseAttachment.vue';
import FileIcon from 'next/icon/FileIcon.vue';

const { attachments, messageType, isEmailInbox } = useMessageContext();

const { t } = useI18n();

const url = computed(() => {
  return attachments.value[0].dataUrl;
});

const fileName = computed(() => {
  if (url.value) {
    const filename = url.value.substring(url.value.lastIndexOf('/') + 1);
    return filename || t('CONVERSATION.UNKNOWN_FILE_TYPE');
  }
  return t('CONVERSATION.UNKNOWN_FILE_TYPE');
});

const fileType = computed(() => {
  return fileName.value.split('.').pop();
});

// Telegram (Bot /sendVoice + MTProto voice_note) sends ALL audio out as a voice
// memo. When the upload's content-type was ambiguous (.ogg/.opus/.m4a often
// arrive as application/ogg or octet-stream) the attachment lands here as a
// generic "file" — so for an OUTGOING audio attachment, relabel the bubble to
// make clear it left as a real voice message to the contact (filename kept).
const AUDIO_EXTS = ['ogg', 'oga', 'opus', 'mp3', 'm4a', 'aac', 'wav', 'flac', 'amr'];
const isOutgoingVoice = computed(() => {
  const ext = String(fileType.value || '').split(/[?#]/)[0].toLowerCase();
  return (
    messageType.value === MESSAGE_TYPES.OUTGOING &&
    !isEmailInbox?.value &&
    AUDIO_EXTS.includes(ext)
  );
});

const senderTranslationKey = computed(() =>
  isOutgoingVoice.value
    ? 'CONVERSATION.SHARED_ATTACHMENT.VOICE'
    : 'CONVERSATION.SHARED_ATTACHMENT.FILE'
);
</script>

<template>
  <BaseAttachmentBubble
    icon="i-teenyicons-user-circle-solid"
    icon-bg-color="bg-n-alpha-3 dark:bg-n-alpha-white"
    :sender-translation-key="senderTranslationKey"
    :content="decodeURI(fileName)"
    :action="{
      href: url,
      label: $t('CONVERSATION.DOWNLOAD'),
    }"
  >
    <template #icon>
      <FileIcon :file-type="fileType" class="size-4" />
    </template>
    <template #default>
      <div class="truncate text-sm text-n-slate-11">{{ decodeURI(fileName) }}</div>
      <div v-if="isOutgoingVoice" class="text-xs text-n-slate-10">
        {{ $t('CONVERSATION.SHARED_ATTACHMENT.VOICE_HINT') }}
      </div>
    </template>
  </BaseAttachmentBubble>
</template>
